import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import candidateService from "@/services/candidateService";
import useToastStore from '@/components/Common/Toast/toastStore';
import {formattedDate} from "@/utils/helpers";
import Loader from '../Loader';
import DialogBox from '@/components/Common/DialogBox';
import usePaginationStore from "@/hooks/stores/usePaginationStore";
import Pagination from "../Common/Pagination";

const CandidateTable = ({ filteredCandidates, isLoading, handleSort, sortConfig, setSelectedCandidate, setIsModalOpen, onCandidatesDelete=null, serverPagination, onPageChange, onPerPageChange }) => {
  const navigate = useNavigate();
  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleteButtonDisabled, setIsDeleteButtonDisabled] = useState(false);
  const { showToast } = useToastStore;
  const { getPerPage, setPerPage } = usePaginationStore();
  const displayColumns = [
    { label: 'Name', column: 'name' },
    { label: 'Email', column: 'email' },
    { label: 'Applied Jobs', column: 'applied_jobs' },
    { label: 'Last Applied Date', column: 'last_applied_date' }
  ];

  const handleSelectAll = (selectAll) => {
    setAllSelected(selectAll);
    if (selectAll) {
      const newSelectedCandidates = filteredCandidates.reduce((acc, candidate) => {
        acc[candidate.id] = true;
        return acc;
      }, {});
      setSelectedCandidates(newSelectedCandidates);
    }
    else{
      setSelectedCandidates({});
    }
    // setAllSelected(!allSelected);
    // const newSelectedCandidates = filteredCandidates.reduce((acc, candidate) => {
    //   acc[candidate.id] = !allSelected;
    //   return acc;
    // }, {});
    // setSelectedCandidates(newSelectedCandidates);
  };

  const handleSelectCandidate = (id) => {
    setSelectedCandidates(prev => {
      let newSelectedCandidates;
      if (prev[id]) {
        const { [id]: _, ...rest } = prev;
        newSelectedCandidates = rest;
      } else {
        newSelectedCandidates = { ...prev, [id]: true };
      }

      // Check if all checkboxes are selected
      const allSelected = Object.keys(newSelectedCandidates).length === filteredCandidates.length;
      setAllSelected(allSelected);

      return newSelectedCandidates;
    });
  };

  const handleBulkDelete = () => {
    const candidateIds = Object.keys(selectedCandidates).filter(id => selectedCandidates[id]);
    if (candidateIds.length === 0) return; // No candidates selected
    setShowDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    const candidateIds = Object.keys(selectedCandidates).filter(id => selectedCandidates[id]);
    try {
      // Uncomment the following line to perform the bulk delete
      await candidateService.bulkDeleteCandidates(candidateIds);
      
      // Update the filteredCandidates state to remove deleted candidates
      if (onCandidatesDelete) {
        onCandidatesDelete(candidateIds); // Ensure this function updates the state in Candidates.jsx
      }
      setSelectedCandidates({}); // Clear selected candidates after deletion
    } catch (error) {
      console.error("Failed to delete candidates in bulk:", error.message);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleBulkDeleteClose = () => {
    setShowDeleteDialog(false);
    setSelectedCandidates({});
  };

  const confirmDelete = async () => {
    if (candidateToDelete) {
      try {
        setIsDeleteButtonDisabled(true);
        await deleteCandidate(candidateToDelete.id);
        showToast({
          message: "User deleted successfully!",
          type: "success"
        });
        setCandidateToDelete(null);
      } catch (error) {
        console.error("Failed to delete candidate:", error.message);
          showToast({
          message: "Failed to delete user",
          type: "error"
        });
      } finally {
        setIsDeleteButtonDisabled(false);
      }
    }
  };
  const deleteCandidate = async (id) => {
    try {
       const result = await candidateService.deleteCandidate(id); // Call deleteCandidate
      //console.log("Candidate deleted:", result);
      if (onCandidatesDelete) {
        onCandidatesDelete([id]);
      }
    } catch (error) {
      throw new Error(`Failed to delete candidate ${id}: ` + error.message);
    }
  };

  const gridTemplate = "grid-cols-[60px_1.5fr_1.5fr_1fr_1fr_120px]";

  // Custom Checkbox Component
  const CustomCheckbox = ({ checked, onChange }) => (
    <div 
      onClick={(e) => {
        e.stopPropagation();
        onChange(!checked);
      }}
      className={`w-5 h-5 relative rounded-md border flex items-center justify-center transition-all cursor-pointer ${
        checked 
          ? 'bg-secondary border-secondary' 
          : 'bg-card border-input'
      }`}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 3L4.5 8.5L2 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );

  return (
    <div className="relative w-full">
      {selectedCandidates && Object.keys(selectedCandidates).length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center justify-between space-x-8 bg-card shadow-2xl border border-border px-8 py-4 rounded-full">
            <span className="text-sm font-semibold text-foreground">
              {Object.keys(selectedCandidates).filter((id) => selectedCandidates[id]).length} candidates selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-full transition-all flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Main Table Container */}
      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm">
        {/* Table Header */}
        <div className={`grid ${gridTemplate} items-center bg-muted border-b border-border py-3 px-2`}>
          <div className="flex justify-center">
            <CustomCheckbox 
              checked={allSelected}
              onChange={(val) => handleSelectAll(val)}
            />
          </div>
          <div 
            className="flex items-center gap-1 cursor-pointer select-none px-4"
            onClick={() => handleSort('name')}
          >
            <span className="text-sm font-semibold text-muted-foreground">Name</span>
            <span className="text-muted-foreground text-xs">↓</span>
          </div>
          <div className="text-sm font-semibold text-muted-foreground px-4 cursor-pointer" onClick={() => handleSort('email')}>
            Email
          </div>
          <div className="text-sm font-semibold text-muted-foreground px-4 cursor-pointer" onClick={() => handleSort('last_applied_date')}>
            Last Applied
          </div>
          <div className="text-sm font-semibold text-muted-foreground px-4 cursor-pointer" onClick={() => handleSort('applied_jobs')}>
            Applied Jobs
          </div>
          <div className="text-sm font-semibold text-muted-foreground text-center">
            Actions
          </div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-border">
          {isLoading ? (
            <div className="py-24 flex flex-col items-center justify-center">
              <Loader className="w-8 h-8" bgColor="bg-transparent" loadingText="Loading candidates..." />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="py-24 text-center">
              <p className="text-muted-foreground text-sm italic">No candidates found for the selected filters</p>
            </div>
          ) : (
            filteredCandidates.map((candidate) => (
              <div
                key={candidate.id}
                onClick={() => navigate(`/candidate/${candidate.id}`)}
                className={`grid ${gridTemplate} items-center py-5 px-2 hover:bg-muted transition-colors cursor-pointer group`}
              >
                {/* Checkbox Column */}
                <div className="flex justify-center">
                  <CustomCheckbox 
                    checked={!!selectedCandidates[candidate.id]}
                    onChange={() => handleSelectCandidate(candidate.id)}
                  />
                </div>

                {/* Name Column */}
                <div className="px-4 font-semibold text-foreground text-sm truncate">
                  {candidate.name}
                </div>

                {/* Email Column */}
                <div className="px-4 text-muted-foreground text-sm font-normal truncate">
                  {candidate.email}
                </div>

                {/* Date Column */}
                <div className="px-4 text-muted-foreground text-sm font-normal">
                  {candidate.last_applied_date ? formattedDate(candidate.last_applied_date) : "Jan 6, 2026"}
                </div>

                {/* Applied Jobs Column */}
                <div className="px-4">
                  <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold bg-[#ECFDF3] text-[#027A48]">
                    {candidate.applied_jobs?.length || 0} Jobs
                  </span>
                </div>

                {/* Action Column */}
                <div className="flex justify-center items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCandidateToDelete(candidate);
                    }}
                    className="flex items-center gap-2 text-muted-foreground hover:text-red-600 font-semibold text-sm transition-colors px-2 py-1"
                  >
                    <div className="w-[18px] h-[18px] rounded-full border-2 border-current transition-colors" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination Container */}
      {!isLoading && filteredCandidates.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={serverPagination?.current_page}
            totalPages={serverPagination?.last_page}
            per_page={serverPagination?.per_page}
            onPerPageChange={onPerPageChange}
            onPageChange={onPageChange}
          />
        </div>
      )}

      <DialogBox
        isOpen={candidateToDelete !== null}
        onClose={() => setCandidateToDelete(null)}
        title="Delete Candidate"
        className="w-[40%] max-md:w-[60%]"
        actionButton={
          <button
            onClick={confirmDelete}
            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
            disabled={isDeleteButtonDisabled}
          >
            {isDeleteButtonDisabled ? "Deleting..." : "Delete"}
          </button>
        }
      >
        <p className="text-base text-muted-foreground">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-foreground">
            {candidateToDelete?.name}
          </span>
          ? This action cannot be undone.
        </p>
      </DialogBox>
    </div>
  );
};

export default CandidateTable;